import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';

import { GET_WALLET_ALL_NFTs } from '@/utils/queryKey';
import { INFTsByWallet } from 'simplehash_api/types/simpleHash';
import { getWalletAllNFTs } from 'simplehash_api/walletApi';
import { useWhitelistContract } from 'smart-contracts/contracts';

type SelectType<T = number> = (data: { walletCollection: INFTsByWallet['result']; nftUsdBalance: number }) => T;
type SelectReturnType<T = undefined> = [T] extends [undefined] ? number : T;

type TUseWalletAllNFTsProps<T> = { select?: SelectType<T>; contractAddresses?: string[]; checkWhiteList?: boolean; cachedData?: boolean; dontIncludePrice?: boolean; enabled?: boolean };

type TUseSpecificWalletAllNFTs<T> = TUseWalletAllNFTsProps<T> & {
  walletAddress: `0x${string}` | undefined;
};

export const useSpecificWalletAllNFTs = <T = number>({ walletAddress, select, checkWhiteList, cachedData, dontIncludePrice, enabled = true }: TUseSpecificWalletAllNFTs<T>) => {
  const whiteListContract = useWhitelistContract();
  return useQuery({
    queryKey: [GET_WALLET_ALL_NFTs, walletAddress, dontIncludePrice],
    queryFn: async () => {
      const allNfts = await getWalletAllNFTs({ wallet_addresses: walletAddress!, cachedData, dontIncludePrice });
      if (checkWhiteList) {
        const collections: {
          address: string;
          isWhiteListed: boolean;
        }[] = [];

        const results = await Promise.all(
          allNfts.walletCollection.map(async (nft) => {
            const collectionAddress = nft.contract.address;

            const cahcedCollection = collections.find(({ address }) => address === collectionAddress);
            let isWhiteListed: boolean;
            if (cahcedCollection) {
              isWhiteListed = cahcedCollection.isWhiteListed;
            } else {
              try {
                [isWhiteListed] = await whiteListContract!.functions.isWhiteListCollection(collectionAddress);
                collections.push({
                  address: collectionAddress,
                  isWhiteListed,
                });
              } catch (e) {
                isWhiteListed = false;
              }
            }

            return {
              ...nft,
              contract: {
                ...nft.contract,
                isWhiteListed,
              },
            };
          })
        );

        return {
          nftUsdBalance: allNfts.nftUsdBalance,
          walletCollection: results,
        };
      }

      return allNfts;
    },
    enabled: !!walletAddress && enabled,
    select(data): SelectReturnType<T> {
      return select ? (select(data) as SelectReturnType<T>) : (data.nftUsdBalance as SelectReturnType<T>);
    },
    staleTime: Infinity,
  });
};

const useWalletAllNFTs = <T = number>(params?: TUseWalletAllNFTsProps<T>) => {
  const { address: walletAddress } = useAccount();

  return useSpecificWalletAllNFTs({ walletAddress, select: params?.select, checkWhiteList: params?.checkWhiteList, cachedData: params?.cachedData, dontIncludePrice: params?.dontIncludePrice });
};

export default useWalletAllNFTs;
